/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, TableCell, Typography } from '@mui/material';
import { TableWidgetV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import { renderStatus } from 'components/v2/cell-renderers/status';
import {
  MOTIVO_SOLICITACAO_LABELS,
  TIPO_ITEM_LABELS,
  canAlterarItem,
  getItemStatusLabel,
  isItemOutro,
} from './statusLabels';
import ItemFormulario from './ItemFormulario';
import EquipamentosHistoricoDialog from './EquipamentosHistoricoDialog';

const motivoLabel = (item) => {
  const codigo = item?.motivo || '';
  return MOTIVO_SOLICITACAO_LABELS[codigo] || codigo || '—';
};

const tipoChipSx = {
  height: 18,
  fontSize: '0.65rem',
  mt: 0.25,
  width: 'auto',
  maxWidth: 'max-content',
  alignSelf: 'flex-start',
  '& .MuiChip-label': { px: 0.75 },
};

const pedidoSaidaHref = (item) => {
  const id = item?.pedidoEstoqueId;
  if (!id) return null;
  return `/v2/estoque/pedidos?id=${encodeURIComponent(id)}&p=0&s=10`;
};

const PedidoSaidaLink = ({ item }) => {
  const href = pedidoSaidaHref(item);
  if (!href) return null;
  return (
    <Button
      size="small"
      variant="text"
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
    >
      {item.pedidoEstoqueNumero
        ? `Pedido de saída ${item.pedidoEstoqueNumero}`
        : 'Pedido de saída'}
    </Button>
  );
};

const pedidoVinculadoHref = (item) => {
  const id = item?.pedidoId;
  if (!id) return null;
  return `/v2/estoque/pedidos?id=${encodeURIComponent(id)}&p=0&s=10`;
};

const PedidoVinculadoLink = ({ item }) => {
  const href = pedidoVinculadoHref(item);
  if (!href) return null;
  return (
    <Button
      size="small"
      variant="text"
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
    >
      Pedido de almoxarifado
    </Button>
  );
};

PedidoSaidaLink.propTypes = {
  item: PropTypes.object.isRequired,
};

PedidoVinculadoLink.propTypes = {
  item: PropTypes.object.isRequired,
};

const ItensTab = ({
  items,
  requisicao,
  canDecide,
  errors,
  canEditItems,
  catalogoItens,
  onItemsChange,
  onDecidir,
}) => {
  const [openItem, setOpenItem] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [historicoItem, setHistoricoItem] = useState(null);

  const upsertItem = (data) => {
    const exists = items.some((item) => item.id === data.id);
    const next = exists
      ? items.map((item) => (item.id === data.id ? data : item))
      : [...items, data];
    onItemsChange(next);
  };

  const showDecidir = !canEditItems && canDecide;
  const titles = canEditItems
    ? ['Item', 'Descrição', 'Motivo']
    : showDecidir
      ? ['Item', 'Descrição', 'Status', 'Motivo', 'Ações']
      : ['Item', 'Descrição', 'Status', 'Motivo'];

  const renderRows = (item) => (
    <>
      <TableCell>
        <Box sx={{ ...tableV2StackedCellContentSx, alignItems: 'flex-start' }}>
          <Typography variant="body2">{item.nomeItem || '—'}</Typography>
          {!isItemOutro(item) && (TIPO_ITEM_LABELS[item.tipoItem] || item.tipoItem) ? (
            <Chip
              size="small"
              label={TIPO_ITEM_LABELS[item.tipoItem] || item.tipoItem}
              sx={tipoChipSx}
            />
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={tableV2StackedCellContentSx}>
          <Typography variant="body2">
            Quantidade: {item.quantidade ?? '—'}
          </Typography>
          {item.tamanho ? (
            <Typography variant="caption" color="text.secondary">
              Tamanho {item.tamanho}
            </Typography>
          ) : null}
          {item.observacao ? (
            <Typography variant="caption" color="text.secondary">
              {item.observacao}
            </Typography>
          ) : null}
          {item.origem ? (
            <Typography variant="caption" color="text.secondary">
              Origem {item.origem}
            </Typography>
          ) : null}
          <PedidoSaidaLink item={item} />
          <PedidoVinculadoLink item={item} />
          {item.tipoItem === 'EQUIPAMENTO' && requisicao?.id ? (
            <Button
              size="small"
              variant="text"
              onClick={() => setHistoricoItem(item)}
              sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
            >
              Visualizar
            </Button>
          ) : null}
        </Box>
      </TableCell>
      {!canEditItems && (
        <TableCell>
          {renderStatus({
            value: item.status,
            label: getItemStatusLabel(item.status),
          })}
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2">
          {motivoLabel(item)}
        </Typography>
      </TableCell>
      {showDecidir && (
        <TableCell>
          {item.status === 'PENDENTE' ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onDecidir(item)}
            >
              Decidir
            </Button>
          ) : canAlterarItem(item, requisicao) ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onDecidir(item)}
            >
              Alterar
            </Button>
          ) : '—'}
        </TableCell>
      )}
    </>
  );

  return (
    <>
      <TableWidgetV2
        rows={items}
        titles={titles}
        headerTitle="Itens"
        renderRows={renderRows}
        hasAdd={canEditItems}
        hasRowActions={canEditItems}
        showEdit={canEditItems}
        showDelete={canEditItems}
        onAdd={() => {
          setSelectedItem(null);
          setOpenItem(true);
        }}
        onEdit={(itemId) => {
          setSelectedItem(items.find((item) => item.id === itemId) || null);
          setOpenItem(true);
        }}
        onDelete={(itemId) => onItemsChange(items.filter((item) => item.id !== itemId))}
        showEmptyState
        emptyStateTitle="Nenhum item"
        emptyStateMessage={
          canEditItems
            ? 'Adicione ao menos um item do catálogo.'
            : 'Esta requisição não tem itens.'
        }
        emptyStateSize="compact"
        showError={!!(errors && errors.items)}
      />
      <EquipamentosHistoricoDialog
        open={!!historicoItem}
        requisicaoId={requisicao?.id}
        item={historicoItem}
        onClose={() => setHistoricoItem(null)}
      />
      {canEditItems && (
        <ItemFormulario
          open={openItem}
          selectedItem={selectedItem}
          catalogoItens={catalogoItens}
          itensExistentes={items}
          onClose={() => {
            setOpenItem(false);
            setSelectedItem(null);
          }}
          onSave={upsertItem}
        />
      )}
    </>
  );
};

ItensTab.propTypes = {
  items: PropTypes.array,
  requisicao: PropTypes.object,
  canDecide: PropTypes.bool,
  errors: PropTypes.object,
  canEditItems: PropTypes.bool,
  catalogoItens: PropTypes.array,
  onItemsChange: PropTypes.func,
  onDecidir: PropTypes.func,
};

ItensTab.defaultProps = {
  items: [],
  requisicao: {},
  canDecide: false,
  errors: {},
  canEditItems: false,
  catalogoItens: [],
  onItemsChange: () => {},
  onDecidir: () => {},
};

export default ItensTab;
